/** @format */

import { Injectable, inject } from '@angular/core';
import { Observable, catchError, map, throwError } from 'rxjs';
import {
	SocalContentPriceData,
	SocialContentData,
	SocialContentImageData,
	SocialContentMediaItem,
	SocialContentProductItem,
	SocialContentResponse,
	SocialContentResponseMediaItem,
	SocialContentResponseProductItem,
	SocialContentResponseProductItemPrice
} from '../model';

import { HttpErrorResponse } from '@angular/common/http';
import { Logger } from '@logger';
import { SocialContentDao } from '../dao/social-content.dao';
import { SocialContentServiceRequestKey } from '../model/social-content.dao.enum';

@Injectable({
	providedIn: 'root'
})
export class SocialContentService {
	private readonly socialContentDao = inject(SocialContentDao);
	private readonly logger = inject(Logger);

	private mapSocialContentResponse(response: SocialContentResponse): SocialContentData {
		const items = response.data.items.map((socialContentItem) => {
			return {
				metadata: socialContentItem.source,
				media: this.getMediaItems(socialContentItem.media),
				products: this.getProducts(socialContentItem.products)
			};
		});
		return {
			items,
			resultsCount: response.data.resultsCount
		};
	}

	private parseMediaImages(mediaItem: SocialContentImageData): SocialContentMediaItem {
		return {
			original: mediaItem.original?.link,
			small: mediaItem.small?.link,
			medium: mediaItem.medium?.link,
			large: mediaItem.large?.link,
			extraLarge: mediaItem.extraLarge?.link,
			smallSquare: mediaItem.smallSquare?.link,
			mediumSquare: mediaItem.mediumSquare?.link,
			largeSquare: mediaItem.largeSquare?.link,
			extraLargeSquare: mediaItem.extraLargeSquare?.link
		};
	}

	private getMediaItems(mediaItem: SocialContentResponseMediaItem): SocialContentMediaItem {
		if (mediaItem.type === 'video' && mediaItem.poster) {
			const poster = mediaItem.poster;
			return this.parseMediaImages(poster);
		}

		return this.parseMediaImages(mediaItem);
	}

	private getPrice(priceData: SocialContentResponseProductItemPrice): SocalContentPriceData {
		return {
			value: priceData.value,
			display: `${priceData.currencySymbol}${priceData.display}`
		};
	}

	private getProducts(products: SocialContentResponseProductItem[]): SocialContentProductItem[] {
		return products.map((product) => {
			return {
				id: product.id,
				name: product.name,
				images: product.images.map((image) => this.parseMediaImages(image)),
				link: product.link,
				metadata: { ...product.metadata },
				spatialTag: { ...product.spatialTag },
				price: product.price ? this.getPrice(product.price) : undefined
			};
		});
	}

	/**
	 * @description
	 * Get social content data from BazaarVoice for a given product by ID / SKU for tagged social image content
	 */
	getSocialContentByProductId$(productId: string, limit = 10): Observable<SocialContentData> {
		return this.socialContentDao.getSocialContentProductId$(productId, limit).pipe(
			map((response) => this.mapSocialContentResponse(response)),
			catchError((error: HttpErrorResponse) => {
				this.logger.dynamicallyLog(
					`${SocialContentServiceRequestKey.BYPRODUCTID}`,
					productId,
					error
				);
				return throwError(() => error);
			})
		);
	}

	/**
	 * @description
	 * Get social content data from BazaarVoice for a given product by URL for tagged social image content
	 */
	getSocialContentByProductUrl$(productUrl: string, limit = 10): Observable<SocialContentData> {
		return this.socialContentDao.getSocialContentProductUrl$(productUrl, limit).pipe(
			map((response) => this.mapSocialContentResponse(response)),
			catchError((error: HttpErrorResponse) => {
				this.logger.dynamicallyLog(
					`${SocialContentServiceRequestKey.BYPRODUCTURL}`,
					productUrl,
					error
				);
				return throwError(() => error);
			})
		);
	}
}
